import { Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Report, ReportTemplate, ReportTemplateTypes } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import TitleBox from "../../components/TitleBox";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import { StyledLink } from "../../components/utility/StyledLink";
import ReportsContext from "../../contexts/ReportsContext";
import useReports from "../../hooks/data/useReports";
import useReportTemplates from "../../hooks/data/useReportTemplates";
import { getScheduleString } from "../../utility/EnumValueGetters";
import EmailOnlyReports from "./EmailOnlyReports";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";

const useReportColumns = (templates: ReportTemplate[]) => {
    const reportColumns = useMemo(() => {
        const columns: GridColDef<Report>[] = [
            {
                headerName: 'Name',
                field: 'name',
                flex: 1,
                renderCell: (params) => {
                    return (
                        <StyledLink 
                            to={`/reports/preview/${params.row.id}`}
                        >
                            {params.formattedValue}
                        </StyledLink>
                    )
                }
            },
            {
                headerName: 'Template',
                field: 'templateId',
                valueGetter: (value) => {return templates?.filter(template => template.id == value )[0]?.name ?? value},
                flex: 1
            },
            {
                headerName: 'Schedule',
                field: 'scheduleType',
                valueGetter: getScheduleString,
                width: 200
            },
            {
                headerName: '',
                field: 'actions',
                renderCell: (params) => (
                    <GridActions 
                        editUrl={`/reports/edit/${params.row.id}`}
                        deleteUrl={`/reports/delete/${params.row.id}`}
                    />
                ),
                width: 100,
                disableExport: true,
                align: 'right'
            }
        ]

        return columns;
    }, [templates])

    return reportColumns;
}

const Reports: FC = () => {
    const emailOnlyTemplateTypes = [
        ReportTemplateTypes.EmailOnlySalesSummary,
        ReportTemplateTypes.EmailOnlyLotSummary
    ]
    const contextValue = useReports();
    const {data: reports} = contextValue;
    const templatesContextValue = useReportTemplates();
    const {data: reportTemplates} = templatesContextValue;

    const emailOnlyReports = useMemo(() => 
        reports?.filter(report => {
            const templateType = reportTemplates?.find(template => template.id == report.templateId)?.templateType;
            return templateType && emailOnlyTemplateTypes.includes(templateType); 
        })
    , [reports, reportTemplates]);

    const nonEmailOnlyReports = useMemo(() => 
        reports?.filter(report => {
            const templateType = reportTemplates?.find(template => template.id == report.templateId)?.templateType;
            return !templateType || !emailOnlyTemplateTypes.includes(templateType); 
        })
    , [reports, reportTemplates]);

    const reportColumns = useReportColumns(reportTemplates ?? []);

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Reports`,
        exportTitle: `Reports`,
        includeDateTime: true
    }), []);

    return (
        <ReportTemplatesContext.Provider value={templatesContextValue}>
            <ReportsContext.Provider value={contextValue}>
                <SetLinks
                    stageLinkEnabled={false}
                    lotLinkEnabled={false}
                />
                <Outlet />
                <TitleBox
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2
                    }}
                >
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={"/reports/templates"}
                    >
                        Templates
                    </StyledButton>
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={"/reports/create"}
                    >
                        New Report
                    </StyledButton>
                </TitleBox>
                <EmailOnlyReports 
                    reports={emailOnlyReports ?? []}
                />
                <Paper>
                    <CollapsibleTablePremium
                        density="compact"
                        columns={reportColumns}
                        title={"Reports"} 
                        loading={false}
                        rows={nonEmailOnlyReports}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Paper>
            </ReportsContext.Provider>
        </ReportTemplatesContext.Provider>
    );
}

export default Reports;